/**
 * Compiled-in module loaders — the seam for the Module-Federation → single-app
 * consolidation (see docs/todo.md `G1559`–`G1565`).
 *
 * Historically every first-party module was a Module-Federation remote: the
 * shell loaded it via `import('<federatedName>/module')` (the `federatedLoaders`
 * map in App.tsx), which rspack's ModuleFederationPlugin rewrites into a remote
 * fetch. That forced ~33 always-on dev servers and a heavy multi-`remoteEntry`
 * production load.
 *
 * Target: first-party modules are compiled into the shell as `React.lazy` route
 * chunks (one build, one dev server); Module Federation is retained ONLY for the
 * genuine runtime-plugin surface (Marketplace + customer widgets/modules).
 *
 * `localLoaders` is how a migrated module registers its compiled-in entry. The
 * shell prefers a local loader and falls back to the federated remote (see
 * App.tsx: `localLoaders[name] ?? federatedLoaders[name]`), so modules migrate
 * ONE AT A TIME with the app staying green.
 *
 * To migrate a module: (1) add an exact-match (`$`) alias in the shell's
 * rspack.config.ts resolve.alias mapping `@tyndo/<module>/module$` to the
 * module's `src/index.ts`; (2) add an entry here whose key matches the module's
 * `federatedModuleName`, e.g. `production_ui: () => import('@tyndo/production-ui/module')`.
 * rspack then emits the module as a code-split async chunk.
 *
 * `marketplace_ui` is intentionally NOT migrated — it is the runtime-plugin
 * surface and stays on the federated path (G1563).
 */
export type ModuleLoader = () => Promise<unknown>;

/**
 * The canonical set of first-party module federated names that are compiled
 * into the shell as lazy chunks. This list is the SINGLE SOURCE OF TRUTH paired
 * with the `@tyndo/<module>/module$` resolve aliases in
 * apps/shell/rspack.config.ts — every name here MUST have a matching alias, and
 * every compiled-in alias MUST appear here.
 *
 * Typing `localLoaders` as `Record<CompiledInModuleName, ModuleLoader>` (below)
 * turns a missing loader into a COMPILE error rather than a silent runtime
 * federation fallback: drop a module from the map and `tsc` fails on the map
 * literal; add a name here without a loader entry and `tsc` fails too. The
 * runtime aliases are kept in lockstep by the rspack-alias coverage test
 * (registries.test.ts), which fails if the two lists drift.
 *
 * `marketplace_ui` is intentionally absent — it is the genuine runtime-plugin
 * surface and stays on the federated remote path (G1563).
 */
export const COMPILED_IN_MODULE_NAMES = [
  'production_ui',
  'equipment_ui',
  'alarms_ui',
  'batch_ui',
  'quality_ui',
  'lims_ui',
  'planning_ui',
  'inventory_ui',
  'procurement_ui',
  'maintenance_ui',
  'analytics_ui',
  'costing_ui',
  'compliance_ui',
  'integration_ui',
  'admin_ui',
  'copilot_ui',
  'vision_ui',
  'workflow_ui',
  'scada_ui',
  'materials_ui',
  'controls_ui',
  'multisite_ui',
  'finance_ui',
  'sales_ui',
  'hr_ui',
  'crm_ui',
  'plm_ui',
  'digital_twin_ui',
  'designer_ui',
  'sustainability_ui',
  'predictive_ui',
  'collaboration_ui',
  'agent_workforce_ui',
  'edgefleet_ui',
  'teaming_ui',
  'spatial_ui',
  'authoring_ui',
  'voice_ui',
  'lineage_ui',
  'queryvisa_ui',
  'transparencylog_ui',
  'causalattribution_ui',
  'pcfwire_ui',
  'historian_ui',
  'uns_ui',
  'writefirewall_ui',
  'blackoutoutbox_ui',
  'blackboard_ui',
  'capacityexchange_ui',
  'competencepassport_ui',
  'qualificationnetwork_ui',
  'secondsexchange_ui',
  'underwritingpack_ui',
  'productpassport_ui',
  'handshakeledger_ui',
  'digesttreaty_ui',
  'federatedworkorder_ui',
  'motiongenome_ui',
  'plantsandbox_ui',
  'responseatlas_ui',
  'flightsimulator_ui',
] as const;

/** Federated name of a module compiled into the shell as a lazy chunk. */
export type CompiledInModuleName = (typeof COMPILED_IN_MODULE_NAMES)[number];

// Typed as an exact `Record<CompiledInModuleName, …>`: TypeScript requires
// every key of the union to be present (a missing one is a compile error) and
// rejects any key not in the union (a typo'd / stray loader is a compile error).
export const localLoaders: Record<CompiledInModuleName, ModuleLoader> = {
  production_ui: () => import('@tyndo/production-ui/module'),
  equipment_ui: () => import('@tyndo/equipment-ui/module'),
  alarms_ui: () => import('@tyndo/alarms-ui/module'),
  batch_ui: () => import('@tyndo/batch-ui/module'),
  quality_ui: () => import('@tyndo/quality-ui/module'),
  lims_ui: () => import('@tyndo/lims-ui/module'),
  planning_ui: () => import('@tyndo/planning-ui/module'),
  inventory_ui: () => import('@tyndo/inventory-ui/module'),
  procurement_ui: () => import('@tyndo/procurement-ui/module'),
  maintenance_ui: () => import('@tyndo/maintenance-ui/module'),
  analytics_ui: () => import('@tyndo/analytics-ui/module'),
  costing_ui: () => import('@tyndo/costing-ui/module'),
  compliance_ui: () => import('@tyndo/compliance-ui/module'),
  integration_ui: () => import('@tyndo/integration-ui/module'),
  admin_ui: () => import('@tyndo/admin-ui/module'),
  copilot_ui: () => import('@tyndo/copilot-ui/module'),
  vision_ui: () => import('@tyndo/vision-ui/module'),
  workflow_ui: () => import('@tyndo/workflow-ui/module'),
  scada_ui: () => import('@tyndo/scada-ui/module'),
  materials_ui: () => import('@tyndo/materials-ui/module'),
  controls_ui: () => import('@tyndo/controls-ui/module'),
  multisite_ui: () => import('@tyndo/multisite-ui/module'),
  finance_ui: () => import('@tyndo/finance-ui/module'),
  sales_ui: () => import('@tyndo/sales-ui/module'),
  hr_ui: () => import('@tyndo/hr-ui/module'),
  crm_ui: () => import('@tyndo/crm-ui/module'),
  plm_ui: () => import('@tyndo/plm-ui/module'),
  digital_twin_ui: () => import('@tyndo/digital-twin-ui/module'),
  designer_ui: () => import('@tyndo/designer-ui/module'),
  sustainability_ui: () => import('@tyndo/sustainability-ui/module'),
  predictive_ui: () => import('@tyndo/predictive-ui/module'),
  collaboration_ui: () => import('@tyndo/collaboration-ui/module'),
  agent_workforce_ui: () => import('@tyndo/agent-workforce-ui/module'),
  edgefleet_ui: () => import('@tyndo/edgefleet-ui/module'),
  teaming_ui: () => import('@tyndo/teaming-ui/module'),
  spatial_ui: () => import('@tyndo/spatial-ui/module'),
  authoring_ui: () => import('@tyndo/authoring-ui/module'),
  voice_ui: () => import('@tyndo/voice-ui/module'),
  lineage_ui: () => import('@tyndo/lineage-ui/module'),
  queryvisa_ui: () => import('@tyndo/queryvisa-ui/module'),
  transparencylog_ui: () => import('@tyndo/transparencylog-ui/module'),
  causalattribution_ui: () => import('@tyndo/causalattribution-ui/module'),
  pcfwire_ui: () => import('@tyndo/pcfwire-ui/module'),
  historian_ui: () => import('@tyndo/historian-ui/module'),
  uns_ui: () => import('@tyndo/uns-ui/module'),
  writefirewall_ui: () => import('@tyndo/writefirewall-ui/module'),
  blackoutoutbox_ui: () => import('@tyndo/blackoutoutbox-ui/module'),
  blackboard_ui: () => import('@tyndo/blackboard-ui/module'),
  capacityexchange_ui: () => import('@tyndo/capacityexchange-ui/module'),
  competencepassport_ui: () => import('@tyndo/competencepassport-ui/module'),
  qualificationnetwork_ui: () => import('@tyndo/qualificationnetwork-ui/module'),
  secondsexchange_ui: () => import('@tyndo/secondsexchange-ui/module'),
  underwritingpack_ui: () => import('@tyndo/underwritingpack-ui/module'),
  productpassport_ui: () => import('@tyndo/productpassport-ui/module'),
  handshakeledger_ui: () => import('@tyndo/handshakeledger-ui/module'),
  digesttreaty_ui: () => import('@tyndo/digesttreaty-ui/module'),
  federatedworkorder_ui: () => import('@tyndo/federatedworkorder-ui/module'),
  motiongenome_ui: () => import('@tyndo/motiongenome-ui/module'),
  plantsandbox_ui: () => import('@tyndo/plantsandbox-ui/module'),
  responseatlas_ui: () => import('@tyndo/responseatlas-ui/module'),
  flightsimulator_ui: () => import('@tyndo/flightsimulator-ui/module'),
};

/**
 * Narrowing lookup for an arbitrary federated name (e.g. one read off the
 * gateway manifest, typed `string | undefined`). Returns the compiled-in loader
 * when the name is a known {@link CompiledInModuleName}, else `undefined`. Keeps
 * the strict `Record<CompiledInModuleName, …>` typing of `localLoaders` while
 * letting callers probe with a loose runtime string.
 */
export function getLocalLoader(
  federatedName: string | undefined,
): ModuleLoader | undefined {
  if (!federatedName) return undefined;
  return (localLoaders as Record<string, ModuleLoader>)[federatedName];
}
